import { EmptyState } from '@ibe/ui';
import { Construction } from 'lucide-react';

/**
 * A screen that exists so its menu entry can.
 *
 * `navigation.ts` requires every entry to resolve to a real route, and
 * `tests/navigation.test.tsx` renders each one to enforce it — a menu is a
 * promise about what the application can do, and an entry that lands on the
 * not-found page reads as a broken product rather than an unfinished one.
 *
 * This is the honest middle ground: the destination exists, says plainly that
 * the feature is not built, and names what will be there. It is deliberately
 * not a blank page — a blank screen is indistinguishable from a failure, and
 * the support call it produces is "the page is empty".
 *
 * Delete each usage as its real screen lands. A placeholder that outlives its
 * feature is worse than no menu entry at all.
 */
export interface ComingSoonPageProps {
  readonly title: string;
  /** One line on what this screen will do, so the entry is not a mystery. */
  readonly description: string;
}

export function ComingSoonPage({ title, description }: ComingSoonPageProps): React.JSX.Element {
  return (
    <section className="space-y-6">
      {/* An h1, not a styled div: the navigation test asserts every route
          renders one, which is what proves the route resolved to a screen
          rather than to an empty shell. */}
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>

      <EmptyState
        icon={<Construction className="h-8 w-8" />}
        title="Not built yet"
        description={description}
      />
    </section>
  );
}
